import { mean } from 'd3-array';

import { GetTimeSeriesResponse } from '@/utils/defined.types';

import type { CustomTimezones } from '../../../public/static/charting_library';
import { NATIVE_TOKEN_DATA_ADDRESSES } from '../../constants/addresses';
import { TokenResult } from '../../utils/0x/token-registry.types';
import { isTokenResultNativeAsset } from '../../utils/multichain';

export type PriceView = 'usd' | 'pair' | 'pair-inverse';

/**
 * Calculate dynamic precision for small value tokens to show significant figures
 * @param price - The price value to determine precision for
 * @param significantFigures - Number of significant figures to show (default: 3)
 * @param maxDecimalPlaces - Maximum decimal places based on token decimals (default: 18)
 * @returns The number of decimal places to show
 */
export const getDynamicPrecision = (
  price: number | undefined,
  significantFigures: number = 3,
  maxDecimalPlaces: number = 18,
): number => {
  if (!price || price === 0) return 2;
  if (price >= 1) return 2;

  const absPrice = Math.abs(price);
  const magnitude = Math.floor(Math.log10(absPrice));

  const calculatedPrecision = Math.abs(magnitude) + significantFigures - 1;
  return Math.min(calculatedPrecision, maxDecimalPlaces);
};

/**
 * Calculate TradingView pricescale based on price magnitude
 * @param price - The price value to determine pricescale for
 * @param significantFigures - Number of significant figures to show (default: 3)
 * @param maxDecimalPlaces - Maximum decimal places based on token decimals (default: 18)
 * @returns The pricescale value for TradingView
 */
export const getTradingViewPricescale = (
  price: number | undefined,
  significantFigures: number = 3,
  maxDecimalPlaces: number = 18,
): number => {
  const precision = getDynamicPrecision(price, significantFigures, maxDecimalPlaces);
  return Math.pow(10, precision);
};

export const CHART_RANGE_MINUTES = {
  MINUTE: 1,
  FIVE_MINUTES: 5,
  FIFTEEN_MINUTES: 15,
  THIRTY_MINUTES: 30,
  HOUR: 60,
  FOUR_HOURS: 240,
  TWELVE_HOURS: 720,
  DAILY: 1440,
  FIVE_DAYS: 7200,
  WEEKLY: 10080,
  FIFTEEN_DAYS: 21600,
  MONTHLY: 43800,
  TWO_MONTHS: 87600,
  EIGHT_MONTHS: 350400,
  YEARLY: 525960,
  TWO_YEARS: 1051920,
  FOUR_YEARS: 2103840,
  SIXTEEN_YEARS: 8415360,
  MAX: 0,
} as const;

/**
 * Time frame for each candle.
 * Matcha API supports 15S, 30S, 1, 5, 15, 30, 60, 240, 720, 1D and 7D.
 */
export const TIME_FRAMES = {
  FIFTEEN_SECONDS: '15S',
  THIRTY_SECONDS: '30S',
  ONE_MINUTE: '1',
  FIVE_MINUTES: '5',
  FIFTEEN_MINUTES: '15',
  THIRTY_MINUTES: '30',
  ONE_HOUR: '60',
  FOUR_HOURS: '240',
  TWELVE_HOURS: '720',
  DAY: '1D',
  WEEK: '1W',
};

export const RESOLUTION_MINUTES = {
  [TIME_FRAMES.ONE_MINUTE]: CHART_RANGE_MINUTES.MINUTE,
  [TIME_FRAMES.FIVE_MINUTES]: CHART_RANGE_MINUTES.FIVE_MINUTES,
  [TIME_FRAMES.FIFTEEN_MINUTES]: CHART_RANGE_MINUTES.FIFTEEN_MINUTES,
  [TIME_FRAMES.ONE_HOUR]: CHART_RANGE_MINUTES.HOUR,
  [TIME_FRAMES.FOUR_HOURS]: CHART_RANGE_MINUTES.FOUR_HOURS,
  [TIME_FRAMES.TWELVE_HOURS]: CHART_RANGE_MINUTES.TWELVE_HOURS,
  [TIME_FRAMES.DAY]: CHART_RANGE_MINUTES.DAILY,
  [TIME_FRAMES.WEEK]: CHART_RANGE_MINUTES.WEEKLY,
};

/** Pre-existing default backfill window for pair-view chart history. */
const PAIR_BACKFILL_DEFAULT_SECONDS = 30 * 24 * 60 * 60;

/**
 * matcha-api's Codex `getBars` upstream caps a single response at 1500 bars
 * and treats `[from, to]` as inclusive, so the widest window that yields
 * one full call is `(1500 - 1) * barSeconds`. Going beyond it forces
 * matcha-api to chunk into multiple parallel Codex sub-requests, which is
 * billed per call.
 */
const CODEX_MAX_BARS_PER_REQUEST = 1500;

/** Bar duration (seconds) for each TradingView resolution we support. */
const TV_RESOLUTION_BAR_SECONDS: Record<string, number> = {
  [TIME_FRAMES.ONE_MINUTE]: 60,
  [TIME_FRAMES.FIVE_MINUTES]: 300,
  [TIME_FRAMES.FIFTEEN_MINUTES]: 900,
  [TIME_FRAMES.THIRTY_MINUTES]: 1800,
  [TIME_FRAMES.ONE_HOUR]: 3600,
  [TIME_FRAMES.FOUR_HOURS]: 14400,
  [TIME_FRAMES.TWELVE_HOURS]: 43200,
  [TIME_FRAMES.DAY]: 86400,
  [TIME_FRAMES.WEEK]: 604800,
};

/**
 * Max history window (seconds) to pre-fetch for the pair symbol when the
 * user switches a chart to pair / pair-inverse view.
 *
 * Clamps to a single matcha-api Codex `getBars` call at the active resolution
 * so a price-view switch on a 1m or 5m chart doesn't fan out to several
 * Codex calls (or, at 1m, silently drop everything older than ~6 days due to
 * matcha-api's 6-chunk fan-out cap). For resolutions where the pre-existing
 * 30 day default already fits in one call (≥ 30m), behavior is unchanged.
 *
 * TradingView's `getBars` paginates older pair bars on demand as the user
 * scrolls left, so reducing the initial backfill only affects what's loaded
 * up-front, not what the user can ultimately see.
 */
export const getMaxPairBackfillSeconds = (resolution: string): number => {
  const barSeconds = TV_RESOLUTION_BAR_SECONDS[resolution];
  if (!barSeconds) return PAIR_BACKFILL_DEFAULT_SECONDS;
  return Math.min((CODEX_MAX_BARS_PER_REQUEST - 1) * barSeconds, PAIR_BACKFILL_DEFAULT_SECONDS);
};

export const chartTypeMap: Record<number, string> = {
  0: 'Bars',
  1: 'Candles',
  2: 'Line',
  3: 'Area',
  4: 'Renko',
  5: 'Kagi',
  6: 'PointAndFigure',
  7: 'LineBreak',
  8: 'HeikenAshi',
  9: 'HollowCandles',
  10: 'Baseline',
  12: 'HiLo',
  13: 'Column',
  14: 'LineWithMarkers',
  15: 'Stepline',
  16: 'HLCArea',
  19: 'VolCandle',
};

/**
 * Calculates percent change from the first value to the last value.
 * @param chartData - Chart points ordered from oldest to newest.
 * @returns Percent - Change formatted string,
 */
export const getPercentageChange = (chartData: { value: number }[]): number => {
  const firstValue = chartData[0]?.value;
  const lastValue = chartData[chartData.length - 1]?.value;
  if (firstValue === lastValue) {
    return 0.0;
  } else {
    const percentChange = ((lastValue - firstValue) / firstValue) * 100;
    return percentChange;
  }
};
/**
 * Returns an array of item labels for the market view drop down component on the charts.
 * Note: This function does not format the labels.
 * @param activeTokenSymbolFormatted - required
 * @param inactiveTokenSymbolFormatted - optional - If defined, returns 3 items, if not only 1
 * @returns array of `{key : number, label: string}` object for each market view option
 */
export const getMarketViewDropDownItemLabels = ({
  activeTokenSymbolFormatted,
  inactiveTokenSymbolFormatted,
  fiatCurrencyCode = 'USD',
}: {
  activeTokenSymbolFormatted: string;
  inactiveTokenSymbolFormatted?: string;
  fiatCurrencyCode?: string;
}) => {
  const itemLabelObjects = [
    {
      key: 0,
      label: `${activeTokenSymbolFormatted} / ${fiatCurrencyCode}`,
    },
  ];
  if (inactiveTokenSymbolFormatted && activeTokenSymbolFormatted) {
    itemLabelObjects.push(
      {
        key: 1,
        label: `${activeTokenSymbolFormatted} / ${inactiveTokenSymbolFormatted}`,
      },
      {
        key: 2,
        label: `${inactiveTokenSymbolFormatted} / ${activeTokenSymbolFormatted}`,
      },
    );
  }
  return itemLabelObjects;
};

/**
 * Scales the bounds of a range to make sure it's at least a 5% spread
 * This is used to make sure the y axis always shows at least 5% worth of change
 * @param min - Minimum number
 * @param max - Max number
 */
export const scaleDomainToAtLeastMinimumThreshold = (min: number, max: number) => {
  // Find the midpoint between the max and min
  const midpoint = mean([min, max]);
  // Compute the minimum y value 2.5% away from the midpoint to show (total absolute % show is 5% (2.5% on both sides))
  const percent = [0.97, 1.03];
  const percentageAwayFromAvgMin = (midpoint ?? 0) * percent[0];
  const percentageAwayFromAvgMax = (midpoint ?? 0) * percent[1];

  // Here we ensure that our data shows a minimum y-axis of 5% change.
  // Otherwise tiny changes over stable periods show as huge voltaility to the user
  // (eg when a stablecoin changes 0.05% this makes it show stable y values)
  const dataMinToUse = Math.min(min, percentageAwayFromAvgMin);
  const dataMaxToUse = Math.max(max, percentageAwayFromAvgMax);

  return {
    scaledMin: dataMinToUse,
    scaledMax: dataMaxToUse,
  };
};

/**
 * Gets the TradingView symbol for a token
 * @param token - The token result
 * @param chainId - The chain ID
 * @returns The symbol string in format "address:chainId"
 */
export const getTokenSymbol = (
  token: TokenResult | undefined,
  chainId: number,
): string | undefined => {
  if (!token) return undefined;
  return isTokenResultNativeAsset(token)
    ? `${NATIVE_TOKEN_DATA_ADDRESSES[token.chainId]}:${chainId}`
    : `${token.address}:${chainId}`;
};

/**
 * Creates an empty time series data object
 * @returns Empty GetTimeSeriesResponse object
 */
export const createEmptyTimeSeriesData = (): GetTimeSeriesResponse => ({
  s: 'ok',
  t: [],
  o: [],
  h: [],
  l: [],
  c: [],
  volume: [],
});

/**
 * Gets the user's timezone for TradingView
 * @returns The user's timezone or UTC as fallback
 */
export const getUserTimezone = (): CustomTimezones => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone as CustomTimezones;
  } catch {
    return 'Etc/UTC' as CustomTimezones;
  }
};
